'use client'

import { useId, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Modal, ModalPreview } from '@/components/ui/modal'
import type { DeliveredAnnouncement } from '@/lib/announcements/delivery'

import { AnnouncementCta, AnnouncementIcon } from './announcement-parts'
import { useAnnouncementSeen } from './use-announcement-seen'

/**
 * The one-time "What's New" popup (#14619): the image on top when there is
 * one, the icon and title, the text, the CTA and "Maybe later".
 *
 * `preview` renders the same panel in place through `ModalPreview`, for the
 * authoring form (#14953) — no dialog, nothing recorded.
 */
export function AnnouncementModal({
  announcement,
  onSeen,
  onLater,
  onClose,
  onCta,
  preview = false,
}: {
  announcement: DeliveredAnnouncement
  onSeen: () => void
  /** "Maybe later": seen, not dismissed. */
  onLater: () => void
  /** The close control, Escape or the backdrop. */
  onClose: () => void
  onCta: () => void
  preview?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useAnnouncementSeen(ref, !preview, onSeen)

  const header = (
    <AnnouncementModalHeader announcement={announcement} titleId={titleId} />
  )
  const footer = (
    <AnnouncementModalFooter
      announcement={announcement}
      onLater={onLater}
      onCta={onCta}
    />
  )
  const body = (
    <div
      ref={ref}
      data-testid={
        preview ? 'announcement-modal-preview' : 'announcement-modal'
      }
    >
      <AnnouncementModalBody body={announcement.body} />
    </div>
  )

  if (preview) {
    return (
      <ModalPreview ariaLabelledBy={titleId} header={header} footer={footer}>
        {body}
      </ModalPreview>
    )
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      ariaLabelledBy={titleId}
      header={header}
      footer={footer}
    >
      {body}
    </Modal>
  )
}

/** The popup's header: the image on top when there is one, then the icon and the title. */
function AnnouncementModalHeader({
  announcement,
  titleId,
}: {
  announcement: DeliveredAnnouncement
  titleId: string
}) {
  return (
    <div className="flex flex-col gap-3">
      {announcement.imageUrl && (
        <img
          src={announcement.imageUrl}
          alt=""
          className="max-h-56 w-full rounded-md object-cover"
        />
      )}
      <div className="flex items-center gap-2">
        <AnnouncementIcon
          iconName={announcement.iconName}
          className="text-text-link"
        />
        <h2 id={titleId} className="text-lg font-semibold text-text-primary">
          {announcement.title}
        </h2>
      </div>
    </div>
  )
}

/** The popup's text. */
function AnnouncementModalBody({ body }: { body: string }) {
  return (
    <p className="whitespace-pre-line text-sm text-text-secondary">{body}</p>
  )
}

/** The popup's footer: "Maybe later" and the CTA. */
function AnnouncementModalFooter({
  announcement,
  onLater,
  onCta,
}: {
  announcement: DeliveredAnnouncement
  onLater: () => void
  onCta: () => void
}) {
  return (
    <>
      <Button
        variant="ghost"
        onClick={onLater}
        className="pointer-coarse:min-h-[44px]"
      >
        Maybe later
      </Button>
      <AnnouncementCta
        label={announcement.ctaLabel}
        url={announcement.ctaUrl}
        onActivate={onCta}
      />
    </>
  )
}
